import React, { useContext, useState } from "react";
import { IDropzoneProps } from "react-dropzone-uploader";

import { Button, LoadingOverlay } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import {
  ButtonUploadWrapper,
  ButtonWrapper,
  StyleButtonWrapper,
  StyledAccountSelectWrapper,
  StyleDescription,
  StyleDropzoneWrapper,
  StyleFooter
} from "modules/imports/components/Import.styles";
import { CurrentUserContext } from "modules/login/CurrentUserContext";
import { UserRoles } from "modules/users";
import { FileDropzone } from "ui";
import Loader from "ui/feedback/Loader";
import { UploadIcon } from "ui/icons";
import ModalAccountSelect from "ui/inputs/ModalAccountSelect/ModalAccountSelect";
import ErrorModal from "ui/overlays/ErrorModal";
import Modal from "ui/overlays/Modal";
import { ModalTitle } from "ui/styles";
import RoleGuardWrapper from "ui/wrappers/RoleGuardWrapper";
import * as Yup from "yup";

import { postImportSignedUrlService, putImportFileService } from "../api";
import { useCreateImportMutation } from "../hooks";
import { useImportContext } from "./ImportContext";

const uploadTransactionSchema = Yup.object().shape({
  accountId: Yup.string().required("Account is required")
});

export const UploadTransactionFile = () => {
  const { isInternal } = useContext(CurrentUserContext);
  const [process, setProcess] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false); // state controller for error Modal
  const [message, setMessage] = useState(""); // message to display on success or error
  const createImportMutation = useCreateImportMutation();

  const { onUploadFile, file, onSelectedAccountId, onModalOpen, onSelectImportId } = useImportContext();

  const uploadTransactionForm = useForm({
    initialValues: {
      accountId: "",
      accountName: ""
    },
    validate: yupResolver(uploadTransactionSchema)
  });

  const handleUploadFile = async () => {
    if (file?.name === undefined) {
      return false;
    }

    setProcess(true);
    const accountId = uploadTransactionForm.values.accountId;

    try {
      const response = await createImportMutation.mutateAsync({
        filename: file.name,
        accountId
      });

      const importId = response.data.id;

      if (importId) {
        const signedUrl = await postImportSignedUrlService(importId, "transactions");
        await putImportFileService(signedUrl.data, file);
        onSelectImportId(importId);
        onSelectedAccountId(accountId);
        onModalOpen({ open: "mapTransactionFile", active: null });
        setOpenModal(false);
        setProcess(false);
      }
    } catch (e) {
      setProcess(false);
      onUploadFile(undefined);
      setMessage("Upload failed try again.");
      setErrorModal(true);
    }
  };

  const handleChangeStatus: IDropzoneProps["onChangeStatus"] = ({ file }, status) => {
    if (["getting_upload_params", "headers_received", "preparing", "uploading"].includes(status)) {
      setProcess(true);
    } else {
      setProcess(false);
    }
    if (status === "rejected_file_type") {
      setMessage("Accepted File Types: .csv only.");
      setErrorModal(true);
    }
    if (status === "exception_upload") {
      onUploadFile(undefined);
      setMessage("Upload failed try again.");
      setErrorModal(true);
    }
    if (status === "done") {
      onUploadFile(file);
    }
  };

  const handleResetDropzone = () => {
    onUploadFile(undefined);
    setProcess(false);
    setOpenModal(!openModal);
  };

  const onCloseModal = () => {
    setOpenModal(false);
  };

  const handleSubmit: IDropzoneProps["onSubmit"] = (_, allFiles) => {
    onUploadFile(undefined);
    allFiles.forEach((f: { remove: () => void }) => f.remove());
  };

  return (
    <React.Fragment>
      <RoleGuardWrapper
        roles={[UserRoles.ACCOUNT_ADMIN.value, UserRoles.INTERNAL_ADMIN.value, UserRoles.INTERNAL_ANALYST.value]}
      >
        <ButtonUploadWrapper>
          <Button size="lg" onClick={() => setOpenModal(!openModal)} leftIcon={<UploadIcon />}>
            Upload New Import
          </Button>
        </ButtonUploadWrapper>
      </RoleGuardWrapper>
      <Modal opened={openModal} handleClose={onCloseModal} setOpened={setOpenModal} withCloseButton>
        <form onSubmit={uploadTransactionForm.onSubmit(() => handleUploadFile())}>
          <ModalTitle>Add Transactions File</ModalTitle>
          <StyleDescription>
            Upload a new Transaction File. The file must be a valid CSV. The CSV must include the columns for the
            following data: Date (must be a valid date), Commodity Name, Supplier Name, Cost Center, and Total Cost
            (must be a valid monetary amount). The columns can be named anything you would like. You will be asked to
            map the columns in the upload to standard columns after uploading the file.
          </StyleDescription>
          <StyledAccountSelectWrapper isInternal={isInternal}>
            <ModalAccountSelect
              value={uploadTransactionForm.values.accountId}
              errorMsg={uploadTransactionForm.getInputProps("accountId").error}
              onChange={(accountId: string, accountName: string) => {
                uploadTransactionForm.setValues({ accountId, accountName });
                onSelectedAccountId(accountId);
              }}
            />
          </StyledAccountSelectWrapper>
          <LoadingOverlay visible={process || createImportMutation.isLoading} overlayBlur={2} loader={<Loader />} />
          <StyleDropzoneWrapper>
            <FileDropzone onSubmit={handleSubmit} onChangeStatus={handleChangeStatus} />
          </StyleDropzoneWrapper>
          <StyleFooter>
            <ButtonWrapper>
              <Button
                disabled={process || createImportMutation.isLoading}
                onClick={() => handleResetDropzone()}
                variant="default"
              >
                Cancel
              </Button>
            </ButtonWrapper>
            <StyleButtonWrapper>
              <ButtonWrapper>
                <Button type="submit" disabled={process || file?.name === undefined || createImportMutation.isLoading}>
                  Upload
                </Button>
              </ButtonWrapper>
            </StyleButtonWrapper>
          </StyleFooter>
        </form>
      </Modal>
      <ErrorModal
        openModal={errorModal}
        setOpenModal={setErrorModal}
        onClose={() => setErrorModal(false)}
        message={message}
      />
    </React.Fragment>
  );
};
